import { watchDebounced, watchDeep } from "@vueuse/core";
import { computed, ref, watch } from "vue";
import { insertAt, removeAt, replaceAt } from "#ce/utils/array";
import { defineStore } from "#ce/utils/store";
import { createLayoutBuilderApi } from "#ce/components/SpLayoutBuilder/api/layout-builder";
import { useIntercom } from "#ce/components/SpLayoutBuilder/composables/useIntercom";
import { Context } from "#ce/components/SpLayoutBuilder/constants";
import { Row } from "#ce/components/SpLayoutBuilder/models/row";

const EventType = {
  SETTINGS_CHANGED: "intercom:settings-changed",
  LOAD_LAYOUT: "intercom:load-layout",
  LAYOUT_LOADED: "intercom:layout-loaded",
  ROWS_UPDATED: "intercom:rows-updated",
  ROW_SELECTED: "intercom:row-selected",
  ROW_UPDATED: "intercom:row-updated",
};

/**
 * Store for the layout builder.
 * It allows to communicate with the parent and the iframes.
 * Depends on the context, the store will be used to post messages to the parent or the iframes.
 *
 * @example
 * const { settings, selectedRowId } = useLayoutBuilderStore({ context: "iframe" });
 *
 * @param {Object} options
 * @param {String} options.context - context of the store
 * @returns {Object}
 */
export const useLayoutBuilderStore = (options = {}) => {
  const isIframe = options.context === Context.IFRAME;
  const isParent = options.context === Context.PARENT;

  const api = createLayoutBuilderApi();

  const { dispatchMessage, onMessage } = useIntercom({ isIframe, iframeName: "layout-builder" });

  const store = defineStore("layout-builder", () => {
    const rows = ref([]);
    const selectedRowId = ref(undefined);
    const selectedRow = ref(undefined);
    const permitDeleteRow = computed(() => rows.value.length > 1);
    const mostRecentRowId = ref(undefined);

    const settings = ref({});

    let persistedLayout;

    if (isIframe) {
      watch(selectedRowId, (value) => (selectedRow.value = findRowById(value)));
      watch(selectedRow, (value) => {
        selectedRowId.value = value?.id;
        dispatchRowSelectedMessage(value);
      });

      watchDebounced(rows, dispatchRowsUpdatedMessage, { debounce: 1000, deep: true });

      onMessage(EventType.LAYOUT_LOADED, handleLayoutLoaded);
    }

    if (isParent) {
      watchDeep(settings, () => {
        saveData();
        dispatchSettingsChangedMessage();
      });

      watchDeep(selectedRow, (value) => {
        if (value) {
          dispatchRowUpdatedMessage(value);
        } else {
          dispatchRowSelectedMessage(null);
        }
      });

      // As we fetch the data from an hidden input element in the parent document,
      // we need to listen to the message and load the data via the ContextMenu component
      // which is the parent of the iframe.
      // This is a workaround until we rewrite page builder with a proper API.
      // TODO: remove this once we rewrite page builder with a proper API.
      onMessage(EventType.LOAD_LAYOUT, loadData);
      onMessage(EventType.ROWS_UPDATED, handleRowsUpdated);
    }

    onMessage(EventType.SETTINGS_CHANGED, handleSettingsChanged);
    onMessage(EventType.ROW_SELECTED, handleRowSelected);
    onMessage(EventType.ROW_UPDATED, handleRowUpdated);

    function dispatchRowsUpdatedMessage(rows) {
      dispatchMessage({
        type: EventType.ROWS_UPDATED,
        payload: JSON.stringify(rows),
      });
    }

    function handleRowsUpdated(payload) {
      rows.value = JSON.parse(payload);
      saveData();
    }

    function dispatchSettingsChangedMessage() {
      dispatchMessage({
        type: EventType.SETTINGS_CHANGED,
        payload: JSON.stringify(settings.value),
      });
    }

    function dispatchRowSelectedMessage(row) {
      dispatchMessage({
        type: EventType.ROW_SELECTED,
        payload: JSON.stringify(row),
      });
    }

    function dispatchRowUpdatedMessage(row) {
      dispatchMessage({
        type: EventType.ROW_UPDATED,
        payload: JSON.stringify(row),
      });
    }

    function dispatchLayoutLoadedMessage() {
      dispatchMessage({
        type: EventType.LAYOUT_LOADED,
        payload: JSON.stringify(getLayout()),
      });
    }

    function handleLayoutLoaded(payload) {
      const layout = JSON.parse(payload);
      updateLayout(layout);
    }

    function handleSettingsChanged(payload) {
      settings.value = JSON.parse(payload);
    }

    /**
     * Handle the row selected message.
     *
     * If the row is already selected, do nothing.
     * If the row is not selected, select it.
     * If event payload is undefined or null - which means the row is deselected in the UI, deselect the row.
     *
     * @param {string} payload - The payload of the message
     */
    function handleRowSelected(payload) {
      const row = JSON.parse(payload ?? "null");

      if (!row?.id) {
        selectedRow.value = undefined;
        return;
      }

      if (row.id !== selectedRowId.value) {
        selectedRow.value = row;
      }
    }

    function handleRowUpdated(payload) {
      const row = JSON.parse(payload);
      updateRow(row);
    }

    function insertRow(row, index) {
      mostRecentRowId.value = row.id;
      rows.value = insertAt(rows.value, index, row);
    }

    function createAndInsertNewRow(attributes, index) {
      insertRow(new Row(attributes), index);
    }

    function findRowById(id) {
      return rows.value.find((row) => row.id === id);
    }

    function findRowIndexById(id) {
      return rows.value.findIndex((row) => row.id === id);
    }

    function updateRow(row) {
      const index = findRowIndexById(row.id);
      if (index !== -1) {
        rows.value = replaceAt(rows.value, index, row);
      }
    }

    function deselectRow() {
      selectedRow.value = undefined;
    }

    function deleteRow(id) {
      if (!permitDeleteRow.value) {
        return;
      }

      if (selectedRowId.value === id) {
        deselectRow();
      }

      const index = findRowIndexById(id);
      if (index !== -1) {
        rows.value = removeAt(rows.value, index);
      }
    }

    function updateLayout(layout) {
      rows.value = layout.rows;
      settings.value = layout.settings;
    }

    function getLayout() {
      return {
        rows: rows.value,
        settings: settings.value,
      };
    }

    /**
     * Load the layout data from the parent document or the API.
     * If the store is in iframe context, it will dispatch a message to the parent document
     * to load the data.
     * If the store is in parent context, it will fetch the data from the API.
     * TODO: remove this once we rewrite page builder with a proper API.
     */
    async function loadData() {
      if (isIframe) {
        dispatchMessage({ type: EventType.LOAD_LAYOUT });
      } else {
        const layout = await api.find();
        updateLayout(layout);
        persistedLayout = JSON.stringify(getLayout());
        dispatchLayoutLoadedMessage();
      }
    }

    async function saveData() {
      const layout = getLayout();
      const serialized = JSON.stringify(layout);
      // hydration echoes re-save the loaded layout; only genuine changes may signal edits
      const dispatchChangeEvent = serialized !== persistedLayout;

      persistedLayout = serialized;
      await api.save(layout, { dispatchChangeEvent });
    }

    return {
      // State
      settings,
      rows,
      selectedRowId,
      selectedRow,
      permitDeleteRow,
      mostRecentRowId,

      // Actions
      insertRow,
      createAndInsertNewRow,
      findRowById,
      deselectRow,
      deleteRow,
      loadData,
      saveData,
    };
  });

  return store();
};
